import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { FileContractIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { deleteContractTemplate } from '../../../api/contract-template/delete-contract-template'
import { getContractTemplate } from '../../../api/contract-template/get-contract-template'
import { updateContractTemplate } from '../../../api/contract-template/update-contract-template'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import { ContractTemplateForm } from '../../../components/pages/contract-template/contract-template-form'
import { Paths } from '../../../lib/constants/path'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'

export function ContractTemplateUpdatePage() {
  const { navigate, back } = useNavigator()
  const { id } = useParams<{ id: string }>()

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Identifiant de modèle de contrat invalide" />

  const { data, isPending, refetch } = useQuery({
    queryFn: () => getContractTemplate(parseInt(id)),
    queryKey: ['contract-template', id]
  })

  const { mutateAsync: updateContractTemplateMutations } = useMutation({
    mutationFn: updateContractTemplate,
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      toast.success('Modèle de contrat modifié avec succès')
    }
  })

  const { mutateAsync: deleteContractTemplateMutations } = useMutation({
    mutationFn: deleteContractTemplate,
    onSuccess: () => navigate(Paths.CONTRACT_TEMPLATE_LIST_PAGE),
    onError: showAxiosError
  })

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier un modèle de contrat'} icon={FileContractIcon} />
      <div className={'h-4'} />
      <Box>
        <ContractTemplateForm
          onDelete={async () => {
            await deleteContractTemplateMutations([parseInt(id)])
          }}
          defaultValues={{ ...data, countryIds: data.countries.map((country) => country.id) }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.CONTRACT_TEMPLATE_LIST_PAGE),
            onUpdate: (values) => updateContractTemplateMutations({ id: parseInt(id), ...values }),
            onUpdateThenCreate: async (values) => {
              await updateContractTemplateMutations({ id: parseInt(id), ...values })
              navigate(Paths.CONTRACT_TEMPLATE_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await updateContractTemplateMutations({ id: parseInt(id), ...values })
              navigate(Paths.CONTRACT_TEMPLATE_LIST_PAGE, { replace: true })
            }
          })}
        />
      </Box>
    </div>
  )
}
